import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToneProvider } from './contexts/ToneContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';
import { MaintenanceProvider } from './contexts/MaintenanceContext';
import MaintenanceBanner from './components/MaintenanceBanner';
import Maintenance from './pages/Maintenance';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Ledgers from './pages/Ledgers';
import LedgerDetail from './pages/LedgerDetail';
import Transactions from './pages/Transactions';
import Reports from './pages/Reports';
import Reminders from './pages/Reminders';
import Budgets from './pages/Budgets';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminAuditLogs from './pages/AdminAuditLogs';
import AdminSubscriptions from './pages/AdminSubscriptions';
import AdminMaintenance from './pages/AdminMaintenance';
import AdminSettings from './pages/AdminSettings';
import ProgressPage from './pages/ProgressPage';
import Categories from './pages/Categories';
import Vendors from './pages/Vendors';
import VendorDetail from './pages/VendorDetail';
import HelpHome from './pages/help/HelpHome';
import HelpCategory from './pages/help/HelpCategory';
import HelpArticle from './pages/help/HelpArticle';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ExpenseTrackerIndia from './pages/seo/ExpenseTrackerIndia';
import UdhariManagementApp from './pages/seo/UdhariManagementApp';
import DailyExpenseTracker from './pages/seo/DailyExpenseTracker';
import BudgetPlannerIndia from './pages/seo/BudgetPlannerIndia';
import './App.css';
import './components/Components.css';

/**
 * AdminProtectedRoute - Guards admin routes
 * SECURITY: Enforces app_admin role for all /admin/* routes
 * - Not logged in → /admin/login
 * - Logged in, wrong role → Unauthorized page
 */
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Not authenticated → redirect to admin login
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  // Authenticated but not admin → show unauthorized page
  if (user?.role !== 'app_admin' && user?.role !== 'super_admin') {
    return (
      <div className="unauthorized-container" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#F8F9FA',
        padding: 20,
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: 8,
          padding: 40,
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          maxWidth: 400,
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <h1 style={{ color: '#DC3545', marginBottom: 12 }}>Unauthorized Access</h1>
          <p style={{ color: '#6C757D', marginBottom: 24 }}>
            You do not have permission to access the admin area.
            This page is restricted to administrators only.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <a
              href="/dashboard"
              className="btn btn-primary"
              style={{ textDecoration: 'none' }}
            >
              Go to Dashboard
            </a>
            <a
              href="/admin/login"
              className="btn btn-secondary"
              style={{ textDecoration: 'none' }}
            >
              Admin Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

/**
 * AdminLoginRoute - Special route for admin login
 * - Already logged in as admin → redirect to dashboard
 * - Otherwise → show login
 */
const AdminLoginRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Already authenticated as admin → redirect to admin dashboard
  if (isAuthenticated && (user?.role === 'app_admin' || user?.role === 'super_admin')) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
};

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/maintenance" element={<Maintenance />} />
      <Route path="/" element={<LandingPage />} />
      {/* SEO Landing Pages */}
      <Route path="/expense-tracker-india" element={<ExpenseTrackerIndia />} />
      <Route path="/udhari-management-app" element={<UdhariManagementApp />} />
      <Route path="/daily-expense-tracker" element={<DailyExpenseTracker />} />
      <Route path="/budget-planner-india" element={<BudgetPlannerIndia />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <Signup />
          </PublicRoute>
        }
      />
      {/* Password Reset Routes - Public (Backend handles token validation) */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/admin/forgot-password" element={<ForgotPassword />} />
      <Route path="/admin/reset-password" element={<ResetPassword />} />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/ledgers"
        element={
          <PrivateRoute>
            <Ledgers />
          </PrivateRoute>
        }
      />
      <Route
        path="/ledgers/:id"
        element={
          <PrivateRoute>
            <LedgerDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/transactions"
        element={
          <PrivateRoute>
            <Transactions />
          </PrivateRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <PrivateRoute>
            <Reports />
          </PrivateRoute>
        }
      />
      <Route
        path="/reminders"
        element={
          <PrivateRoute>
            <Reminders />
          </PrivateRoute>
        }
      />
      <Route
        path="/budgets"
        element={
          <PrivateRoute>
            <Budgets />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <Settings />
          </PrivateRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <PrivateRoute>
            <Profile />
          </PrivateRoute>
        }
      />
      {/* Admin Routes - Protected by AdminProtectedRoute */}
      <Route
        path="/admin/login"
        element={
          <AdminLoginRoute>
            <AdminLogin />
          </AdminLoginRoute>
        }
      />
      <Route
        path="/admin"
        element={<Navigate to="/admin/dashboard" replace />}
      />
      <Route
        path="/admin/dashboard"
        element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/subscriptions"
        element={
          <AdminProtectedRoute>
            <AdminSubscriptions />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/activity"
        element={
          <AdminProtectedRoute>
            <AdminAuditLogs />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/maintenance"
        element={
          <AdminProtectedRoute>
            <AdminMaintenance />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <AdminProtectedRoute>
            <AdminSettings />
          </AdminProtectedRoute>
        }
      />
      {/* Legacy admin route - redirect to new path */}
      <Route
        path="/app-admin"
        element={<Navigate to="/admin/dashboard" replace />}
      />
      <Route
        path="/categories"
        element={
          <PrivateRoute>
            <Categories />
          </PrivateRoute>
        }
      />
      <Route
        path="/vendors"
        element={
          <PrivateRoute>
            <Vendors />
          </PrivateRoute>
        }
      />
      <Route
        path="/vendors/:id"
        element={
          <PrivateRoute>
            <VendorDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/progress"
        element={
          <PrivateRoute>
            <ProgressPage />
          </PrivateRoute>
        }
      />
      {/* Help Center - Public (no auth required for SEO & user education) */}
      <Route path="/help" element={<HelpHome />} />
      <Route path="/help/:category" element={<HelpCategory />} />
      <Route path="/help/:category/:slug" element={<HelpArticle />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
      <AuthProvider>
        <ToneProvider>
        <ThemeProvider>
        <Router>
          <MaintenanceProvider>
          <div className="App">
            <MaintenanceBanner />
            <AppRoutes />
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss={false}
              draggable
              pauseOnHover
              limit={3}
              theme="light"
            />
          </div>
          </MaintenanceProvider>
        </Router>
        </ThemeProvider>
        </ToneProvider>
      </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
